import {
    faBullhorn,
    faChartLineUp,
    faDollarCircle,
    faDollarSign,
    faUser,
    faBellConcierge,
    faUsers
} from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Categories } from './InsightStructure';
import store from 'store';
import { report } from 'process';

export enum CardTitles {
    /*CLIENTS_AND_PETS*/
    CLIENT_LIST = 'Client List',
    VACCINE_STATUS = 'Vaccine Status',
    PET_BIRTHDAY = 'Pet Birthday',
    CLIENT_INSIGHTS = 'Client Insights',
    PET_BREAKDOWN = 'Pet Breakdown',
    /*SALES*/
    SALES_SUMMARY = 'Sales Summary',
    SALES_BY_CATEGORY = 'Sales by Category',
    ADD_ONS_INSIGHTS = 'Add-Ons Insights',
    MONTHLY_NET_SALES = 'Monthly Net Sales',
    TRANSACTIONS = 'Transactions',
    SERVICES_INSIGHTS = 'Services Insights',
    PRODUCTS_INSIGHTS = 'Products Insights',
    TAX_LIST = 'Tax List',
    CUSTOM_ITEMS_INSIGHTS = 'Custom Items Insights',
    /*BOOKINGS*/
    BOOKINGS_SUMMARY = 'Bookings Summary',
    BOOKINGS_INSIGHTS = 'Bookings Insights',
    APPOINTMENTS_DETAIL = 'Appointments Detail',
    WEEKLY_BREAKDOWN = 'Weekly Breakdown',
    MONTHLY_BREAKDOWN = 'Monthly Breakdown',
    CANCELLATION_NO_SHOWS = 'Cancellation & No Shows',
    ACTIVITY_LOG = 'Activity Log',
    ROOMS_OPERATING_REPORT = 'Rooms Operating Report',
    /*MARKETING*/
    MARKETING_OVERVIEW = 'Marketing Overview',
    PAID_CHANNELS = 'Paid Channels',
    ACQUISITION_VELOCITY = 'Acquisition Velocity',
    /*STAFF*/
    COMMISSIONS = 'Commissions',
    TIPS = 'Tips',
    TEAM_REVENUE = 'Team Revenue',
    PRODUCTIVITY = 'Productivity'
}

export interface CardData {
    title: CardTitles;
    category: string;
    description: string;
    premium: boolean;
    icon: IconDefinition;
    active: boolean;
}

export const getCards = (marketplaceId?: number): CardData[] => {
    return [
        {
            title: CardTitles.CLIENT_LIST,
            category: Categories.CLIENTS_AND_PETS,
            description: 'A full list of all clients and pets',
            premium: false,
            icon: faUser,
            active: true
        },
        {
            title: CardTitles.VACCINE_STATUS,
            category: Categories.CLIENTS_AND_PETS,
            description: 'Overview of each pet’s current vaccination records',
            premium: true,
            icon: faUser,
            active: true
        },
        {
            title: CardTitles.PET_BIRTHDAY,
            category: Categories.CLIENTS_AND_PETS,
            description: 'A full list of pet birthdays',
            premium: true,
            icon: faUser,
            active: true
        },
        {
            title: CardTitles.CLIENT_INSIGHTS,
            category: Categories.CLIENTS_AND_PETS,
            description: 'Overview of client activity, behavior, and value within a specific time frame',
            premium: true,
            icon: faUser,
            active: true
        },
        {
            title: CardTitles.PET_BREAKDOWN,
            category: Categories.CLIENTS_AND_PETS,
            description: 'A comprehensive breakdown of all pets',
            premium: true,
            icon: faUser,
            active: true
        },
        {
            title: CardTitles.SALES_SUMMARY,
            category: Categories.SALES,
            description: 'Overview of all sales for a specific time period',
            premium: false,
            icon: faDollarCircle,
            active: false
        },
        {
            title: CardTitles.SALES_BY_CATEGORY,
            category: Categories.SALES,
            description: 'Sales performance across different service categories and products',
            premium: true,
            icon: faDollarCircle,
            active: true
        },
        {
            title: CardTitles.ADD_ONS_INSIGHTS,
            category: Categories.SALES,
            description: 'Analysis of revenue generated by each add-on',
            premium: true,
            icon: faDollarCircle,
            active: true
        },
        {
            title: CardTitles.MONTHLY_NET_SALES,
            category: Categories.SALES,
            description: 'Net revenue broken down by each month of the year',
            premium: true,
            icon: faDollarCircle,
            active: true
        },
        {
            title: CardTitles.TRANSACTIONS,
            category: Categories.SALES,
            description: 'Detailed breakdown of Sales, Refunds, and Net Payouts',
            premium: true,
            icon: faDollarCircle,
            active: true
        },
        {
            title: CardTitles.SERVICES_INSIGHTS,
            category: Categories.SALES,
            description: 'Analysis of revenue generated by each service',
            premium: true,
            icon: faDollarCircle,
            active: true
        },
        {
            title: CardTitles.PRODUCTS_INSIGHTS,
            category: Categories.SALES,
            description: 'Analysis of revenue generated by each product',
            premium: true,
            icon: faDollarCircle,
            active: true
        },
        {
            title: CardTitles.TAX_LIST,
            category: Categories.SALES,
            description: 'Detailed breakdown of taxes applied to sales transactions',
            premium: true,
            icon: faDollarCircle,
            active: true
        },
        {
            title: CardTitles.CUSTOM_ITEMS_INSIGHTS,
            category: Categories.SALES,
            description: 'Analysis of revenue generated by each custom item',
            premium: true,
            icon: faDollarCircle,
            active: true
        },
        {
            title: CardTitles.BOOKINGS_SUMMARY,
            category: Categories.BOOKINGS,
            description: 'Overview of all past and upcoming bookings for a specific time period',
            premium: false,
            icon: faBellConcierge,
            active: true
        },
        {
            title: CardTitles.BOOKINGS_INSIGHTS,
            category: Categories.BOOKINGS,
            description: 'Key booking metrics to optimize scheduling efficiency and uncover growth opportunities',
            premium: true,
            icon: faBellConcierge,
            active: true
        },
        {
            title: CardTitles.APPOINTMENTS_DETAIL,
            category: Categories.BOOKINGS,
            description: 'Complete list of all past and future appointments for a specific time period',
            premium: true,
            icon: faBellConcierge,
            active: true
        },
        {
            title: CardTitles.WEEKLY_BREAKDOWN,
            category: Categories.BOOKINGS,
            description: 'Booking metrics broken down by each day of the week',
            premium: true,
            icon: faBellConcierge,
            active: true
        },
        {
            title: CardTitles.MONTHLY_BREAKDOWN,
            category: Categories.BOOKINGS,
            description: 'Booking metrics broken down by each month of the year',
            premium: true,
            icon: faBellConcierge,
            active: true
        },
        {
            title: CardTitles.CANCELLATION_NO_SHOWS,
            category: Categories.BOOKINGS,
            description: 'Summary of all canceled and no show bookings',
            premium: true,
            icon: faBellConcierge,
            active: true
        },
        {
            title: CardTitles.ACTIVITY_LOG,
            category: Categories.BOOKINGS,
            description: 'Detailed list of all actions taken in the business',
            premium: true,
            icon: faBellConcierge,
            active: false
        },
        {
            title: CardTitles.ROOMS_OPERATING_REPORT,
            category: Categories.BOOKINGS,
            description: 'Track occupancy, revenue, and inventory to optimize your operations',
            premium: true,
            icon: faBellConcierge,
            active: true
        },
        {
            title: CardTitles.COMMISSIONS,
            category: Categories.STAFF,
            description: 'Overview of all past and upcoming commissions for a specific time-period',
            premium: true,
            icon: faBellConcierge,
            active: false
        },
        {
            title: CardTitles.TIPS,
            category: Categories.STAFF,
            description: 'Overview of all past and upcoming tips for a specific time-period',
            premium: true,
            icon: faUsers,
            active: false
        },
        {
            title: CardTitles.TEAM_REVENUE,
            category: Categories.STAFF,
            description: "Overview of each team member's revenue performance",
            premium: true,
            icon: faUsers,
            active: false
        },
        {
            title: CardTitles.PRODUCTIVITY,
            category: Categories.STAFF,
            description: 'Overview of your team’s efficiency and performance over a specific time period',
            premium: true,
            icon: faUsers,
            active: false
        },
        {
            title: CardTitles.MARKETING_OVERVIEW,
            category: Categories.MARKETING,
            description: 'Where your new clients come from, and which sources bring clients that stay',
            premium: true,
            icon: faBullhorn,
            active: true
        },
        {
            title: CardTitles.PAID_CHANNELS,
            category: Categories.MARKETING,
            description: 'Spend, cost per client and ROAS for your Google Ads and Meta Ads campaigns',
            premium: true,
            icon: faDollarCircle,
            active: true
        },
        {
            title: CardTitles.ACQUISITION_VELOCITY,
            category: Categories.MARKETING,
            description: 'Monthly cohorts of leads converting into clients, and how fast they book',
            premium: true,
            icon: faChartLineUp,
            active: true
        }
    ];
};
